import { api } from "@/lib/api";
import {
  ApiResponse,
  ErrorResponse,
  Invoice,
  CreateInvoiceDto,
  UpdateInvoiceDto,
} from "@/lib/types";
import { useMutation } from "@tanstack/react-query";

export const useCreateInvoice = (props?: {
  onSuccess?: ({ data }: { data: ApiResponse<Invoice> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (data: CreateInvoiceDto) => {
      return api.post("/invoices", data);
    },
    ...props,
  });
};

export const useUpdateInvoice = (props?: {
  onSuccess?: ({ data }: { data: ApiResponse<Invoice> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (data: UpdateInvoiceDto) => {
      return api.put(`/invoices/${data.id}`, data);
    },
    ...props,
  });
};

export const useDeleteInvoice = (props?: {
  onSuccess?: ({ data }: { data: ApiResponse<null> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (id: string) => {
      return api.delete(`/invoices/${id}`);
    },
    ...props,
  });
};

export const useSubmitInvoice = (props?: {
  onSuccess?: ({ data }: { data: ApiResponse<Invoice> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (id: string) => {
      return api.post(`/invoices/${id}/submit`);
    },
    ...props,
  });
};

export const useApproveInvoice = (props?: {
  onSuccess?: ({ data }: { data: ApiResponse<Invoice> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (id: string) => {
      return api.post(`/invoices/${id}/approve`);
    },
    ...props,
  });
};

export const useRejectInvoice = (props?: {
  onSuccess?: ({ data }: { data: ApiResponse<Invoice> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => {
      return api.post(`/invoices/${id}/reject`, { rejectionReason: reason });
    },
    ...props,
  });
};

export const useReturnInvoiceToDraft = (props?: {
  onSuccess?: ({ data }: { data: ApiResponse<Invoice> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (id: string) => {
      return api.post(`/invoices/${id}/return-to-draft`);
    },
    ...props,
  });
};

export const useCancelInvoice = (props?: {
  onSuccess?: ({ data }: { data: ApiResponse<Invoice> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (id: string) => {
      return api.post(`/invoices/${id}/cancel`);
    },
    ...props,
  });
};

export const useAddInvoicePayment = (props?: {
  onSuccess?: ({ data }: { data: ApiResponse<Invoice> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: ({
      id,
      invoiceId,
      ...payload
    }: CreateInvoicePaymentPayload) => {
      return api.post(`/invoices/${invoiceId ?? id}/payments`, payload);
    },
    ...props,
  });
};

export interface CreateInvoicePaymentPayload {
  id?: string;
  invoiceId?: string;
  transactionDate: string;
  amount: number;
  description?: string;
  reference?: string;
  currencyId: string;
  accountId: string;
  accountConversionRate?: number;
  settlementConversionRate?: number;
  bankCommission?: {
    accountId: string;
    currencyId: string;
    amount: number;
    accountConversionRate?: number;
  };
}

export const useCreateInvoiceAttachment = (props?: {
  onSuccess?: ({ data }: { data: ApiResponse<Invoice> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: ({
      invoiceId,
      file,
      description,
    }: {
      invoiceId: string;
      file: File;
      description?: string;
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      if (description) {
        formData.append("description", description);
      }

      return api.post(`/invoices/${invoiceId}/attachments`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    ...props,
  });
};

export const useDeleteInvoiceAttachment = (props?: {
  onSuccess?: ({ data }: { data: ApiResponse<Invoice> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: ({
      invoiceId,
      attachmentId,
    }: {
      invoiceId: string;
      attachmentId: string;
    }) => {
      return api.delete(`/invoices/${invoiceId}/attachments/${attachmentId}`);
    },
    ...props,
  });
};
